import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query'
import { VoucherTypesApi } from '@trybeapp/sdk.shop'
import {
  ListVoucherTypes,
  ListVoucherTypesRequest,
  ResponseError,
  ShowVoucherType,
  VoucherType,
  VoucherTypesApi as VoucherTypesApiTs,
} from '@trybeapp/sdk'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { usePageQueryParam } from 'hooks/UsePageQueryParam'
import { sdkDateToSystemDateTime, toIsoDate } from 'utilities/DateUtils/dateUtils'
import { apiConfiguration } from 'utilities/TrybeApiClient'
import { ApiResponse, LegacySdkResponseError } from 'api/api.types'
import type { SetOptional } from 'type-fest'

const legacyApi = new VoucherTypesApi()
const api = new VoucherTypesApiTs(apiConfiguration)

type VoucherTypesParams = SetOptional<ListVoucherTypesRequest, 'siteId'>

export const useVoucherTypes = (
  params: VoucherTypesParams = {},
  options?: UseQueryOptions<ListVoucherTypes, ResponseError>
) => {
  const siteId = useCurrentSiteId()
  const request: ListVoucherTypesRequest = { siteId, ...params }

  return useQuery<ListVoucherTypes, ResponseError>(
    ['voucherTypes', request],
    () => api.listVoucherTypes(request),
    options
  )
}

export const useVoucherTypesForPageParam = (params: VoucherTypesParams = {}) => {
  const [page] = usePageQueryParam()
  params.page = page

  return useVoucherTypes(params)
}

interface InfiniteVoucherTypes {
  data: ListVoucherTypes['data']
  page: number
  nextPage: number | undefined
  total: number
}

export const useInfiniteVoucherTypes = (params: Omit<ListVoucherTypesRequest, 'siteId'> = {}) => {
  const siteId = useCurrentSiteId()
  const request: ListVoucherTypesRequest = { siteId, ...params }

  return useInfiniteQuery<InfiniteVoucherTypes, ResponseError>(
    ['infiniteVoucherTypes', request],
    async ({ pageParam: page = 1 }) => {
      const response = await api.listVoucherTypes({ page, ...request })

      return {
        data: response.data,
        page,
        nextPage: response.meta.last_page > page ? page + 1 : undefined,
        total: response.meta.total,
      }
    },
    {
      getNextPageParam: (lastPage) => {
        return lastPage.nextPage
      },
    }
  )
}

export const useGetVoucherType = (
  voucherTypeId: VoucherType['id'],
  options: UseQueryOptions<ShowVoucherType, ResponseError> = {}
) =>
  useQuery<ShowVoucherType, ResponseError>(
    ['voucherType', { voucherTypeId }],
    () => api.getVoucherType({ voucherTypeId }),
    options
  )

export const useOrderedVoucherTypes = (
  params: Record<string, any> = {},
  options: UseQueryOptions<any, LegacySdkResponseError> = {}
) => {
  const siteId = useCurrentSiteId()

  return useQuery<any, LegacySdkResponseError>(
    ['voucherTypes', siteId, params],
    () => legacyApi.getOrderedVoucherTypes(siteId, params),
    options
  )
}

export const useUpdateOrderedVoucherTypes = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<any, LegacySdkResponseError, VoucherType['id'][]>(
    (voucherTypeIds) =>
      legacyApi.updateVoucherTypeOrder({
        voucher_type_ids: voucherTypeIds,
        site_id: siteId,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['voucherTypes'])
      },
    }
  )
}

export const useVoucherAvailabilityRules = (voucherTypeId: VoucherType['id']) =>
  useQuery(['voucherTypeAvailabilityRules', { voucherTypeId }], async () => {
    const response = await legacyApi.voucherTypeAvailabilityRulesIndex(voucherTypeId)

    response.data.forEach((availabilityRule) => {
      availabilityRule.date_from = availabilityRule.date_from
        ? sdkDateToSystemDateTime(availabilityRule.date_from).toJSDate()
        : null

      availabilityRule.date_to = availabilityRule.date_to
        ? sdkDateToSystemDateTime(availabilityRule.date_to).toJSDate()
        : null
    })

    return response
  })

export const useCreateVoucherAvailabilityRule = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<
    ApiResponse<any>,
    LegacySdkResponseError,
    [VoucherType['id'], Record<string, any>]
  >(
    ([voucherTypeId, values]) =>
      legacyApi.voucherTypeAvailabilityRuleStore(voucherTypeId, {
        date_from: toIsoDate(values.date_from),
        date_to: toIsoDate(values.date_to),
        is_available: values.is_available,
        time_from: values.time_from,
        time_to: values.time_to,
        weekdays: values.weekdays,
        site_id: siteId,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['voucherTypeAvailabilityRules'])
      },
    }
  )
}

export const useUpdateVoucherAvailabilityRule = () => {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<any>,
    LegacySdkResponseError,
    [VoucherType['id'], string, Record<string, any>]
  >(
    ([voucherTypeId, ruleId, values]) =>
      legacyApi.voucherTypeAvailabilityRuleUpdate(voucherTypeId, ruleId, {
        date_from: toIsoDate(values.date_from),
        date_to: toIsoDate(values.date_to),
        is_available: values.is_available,
        time_from: values.time_from,
        time_to: values.time_to,
        weekdays: values.weekdays,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['voucherTypeAvailabilityRules'])
      },
    }
  )
}

export const useDeleteVoucherAvailabilityRule = () => {
  const queryClient = useQueryClient()

  return useMutation<void, LegacySdkResponseError, [VoucherType['id'], string]>(
    ([voucherTypeId, ruleId]) =>
      legacyApi.voucherTypeAvailabilityRuleDestroy(voucherTypeId, ruleId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['voucherTypeAvailabilityRules'])
      },
    }
  )
}

export const useCreateVoucherType = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<ApiResponse<VoucherType>, LegacySdkResponseError, Record<string, any>>(
    (voucherType) => legacyApi.voucherTypeStore({ ...voucherType, site_id: siteId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['voucherTypes'])
      },
    }
  )
}

export const useCopyVoucherType = () => {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<VoucherType>,
    LegacySdkResponseError,
    [VoucherType['id'], Record<string, any>]
  >(([copyFromId, voucherType]) => legacyApi.voucherTypeCopy(copyFromId, { voucherType }), {
    onSuccess: () => {
      queryClient.invalidateQueries(['voucherTypes'])
    },
  })
}

export const useUpdateVoucherType = (voucherTypeId: VoucherType['id']) => {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<VoucherType>, LegacySdkResponseError, Record<string, any>>(
    (voucherType) => legacyApi.voucherTypeUpdate(voucherTypeId, voucherType),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['voucherTypes'])
        queryClient.invalidateQueries(['voucherType', { voucherTypeId }])
      },
    }
  )
}

export const useDeleteVoucherType = () => {
  const queryClient = useQueryClient()

  return useMutation<void, LegacySdkResponseError, VoucherType['id']>(
    (voucherTypeId) => legacyApi.voucherTypeDestroy(voucherTypeId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['voucherTypes'])
      },
    }
  )
}

export const useRestoreVoucherType = () => {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<VoucherType>, LegacySdkResponseError, VoucherType['id']>(
    (voucherTypeId) => legacyApi.voucherTypeRestore(voucherTypeId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('voucherTypes')
      },
    }
  )
}
